export interface TopologyWorkbenchChatHistoryBoundary {
  messageId?: string;
  createdAt?: string;
}

export interface TopologyWorkbenchChatHistoryMessage {
  id?: string;
  createdAt?: string;
}

interface FilteredTopologyWorkbenchChatHistory<T> {
  visibleMessages: T[];
  hiddenCount: number;
}

const STORAGE_PREFIX = 'reap.topology.workbench.chat-history-boundary';

const nonEmptyString = (value: unknown): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

const buildStorageKey = (orgId: string, threadId: string) =>
  `${STORAGE_PREFIX}:${encodeURIComponent(orgId)}:${encodeURIComponent(threadId)}`;

export const buildTopologyWorkbenchChatHistoryBoundary = <T extends TopologyWorkbenchChatHistoryMessage>(
  messages: readonly T[]
): TopologyWorkbenchChatHistoryBoundary | null => {
  const last = messages.at(-1);
  if (!last) {
    return null;
  }
  const messageId = nonEmptyString(last.id);
  const createdAt = nonEmptyString(last.createdAt);
  if (!messageId && !createdAt) {
    return null;
  }
  return {
    ...(messageId ? { messageId } : {}),
    ...(createdAt ? { createdAt } : {})
  };
};

export const filterTopologyWorkbenchChatHistory = <T extends TopologyWorkbenchChatHistoryMessage>(
  messages: readonly T[],
  boundary: TopologyWorkbenchChatHistoryBoundary | null | undefined
): FilteredTopologyWorkbenchChatHistory<T> => {
  if (!boundary) {
    return {
      visibleMessages: [...messages],
      hiddenCount: 0
    };
  }

  const boundaryMessageId = nonEmptyString(boundary.messageId);
  if (boundaryMessageId) {
    const boundaryIndex = messages.findIndex((message) => nonEmptyString(message.id) === boundaryMessageId);
    if (boundaryIndex >= 0) {
      return {
        visibleMessages: messages.slice(boundaryIndex + 1),
        hiddenCount: boundaryIndex + 1
      };
    }
  }

  const boundaryCreatedAt = nonEmptyString(boundary.createdAt);
  if (!boundaryCreatedAt) {
    return {
      visibleMessages: [...messages],
      hiddenCount: 0
    };
  }
  const boundaryTime = Date.parse(boundaryCreatedAt);
  if (Number.isNaN(boundaryTime)) {
    return {
      visibleMessages: [...messages],
      hiddenCount: 0
    };
  }
  const visibleMessages = messages.filter((message) => {
    const createdAt = nonEmptyString(message.createdAt);
    if (!createdAt) {
      return false;
    }
    const messageTime = Date.parse(createdAt);
    return Number.isFinite(messageTime) && messageTime > boundaryTime;
  });
  return {
    visibleMessages,
    hiddenCount: Math.max(0, messages.length - visibleMessages.length)
  };
};

export const readTopologyWorkbenchChatHistoryBoundary = (
  orgId: string,
  threadId: string
): TopologyWorkbenchChatHistoryBoundary | null => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return null;
  }
  try {
    const raw = window.localStorage.getItem(buildStorageKey(orgId, threadId));
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const messageId = nonEmptyString(parsed.messageId);
    const createdAt = nonEmptyString(parsed.createdAt);
    if (!messageId && !createdAt) {
      return null;
    }
    return {
      ...(messageId ? { messageId } : {}),
      ...(createdAt ? { createdAt } : {})
    };
  } catch {
    return null;
  }
};

export const writeTopologyWorkbenchChatHistoryBoundary = (
  orgId: string,
  threadId: string,
  boundary: TopologyWorkbenchChatHistoryBoundary | null
): void => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return;
  }
  if (!boundary) {
    window.localStorage.removeItem(buildStorageKey(orgId, threadId));
    return;
  }
  window.localStorage.setItem(
    buildStorageKey(orgId, threadId),
    JSON.stringify({
      ...(boundary.messageId ? { messageId: boundary.messageId } : {}),
      ...(boundary.createdAt ? { createdAt: boundary.createdAt } : {})
    })
  );
};
